import { Button, Icon, Spinner } from '@iwoca/orion';
import clsx from 'clsx';
import { TFile } from 'components/Signup/hooks/useFileUploadReducer';
import styles from './UploadedFiles.module.css';

export type TFileStatus = 'loading' | 'error' | 'success' | 'rejected';

export const UploadedFiles = ({
  files,
  removeFile,
}: {
  files: TFile[];
  removeFile: (id: string) => void;
}) => {
  const renderIcon = (status: TFileStatus) => {
    if (status === 'loading') {
      return <Spinner dataTestId="loading-icon" className={styles.spinner} />;
    }

    const uploadHasErrored = status === 'error' || status === 'rejected';

    return (
      <Icon
        data-testid={`${
          status === 'error'
            ? 'error'
            : status === 'rejected'
              ? 'reject'
              : 'success'
        }-icon`}
        width={20}
        height={20}
        icon={
          uploadHasErrored
            ? 'exclamationTriangleOutline'
            : 'fileWithItemsOutline'
        }
        className={clsx(styles.icon, {
          [styles.errorIcon]: status === 'error',
          [styles.rejectedIcon]: status === 'rejected',
        })}
      />
    );
  };

  return (
    <>
      {files.map((file) => {
        const hasError = file.status === 'error';
        const isRejected = file.status === 'rejected';

        return (
          <div
            key={file.id}
            data-testid={`uploaded-file-${file.id}`}
            className={styles.uploadedFiles}
          >
            <div className={styles.fileNameContainer}>
              <div className={styles.iconContainer}>
                {renderIcon(file.status)}
              </div>
              <p
                className={clsx(styles.name, {
                  [styles.errored]: hasError,
                  [styles.rejected]: isRejected,
                })}
              >
                {file.fileName}
              </p>
              {hasError && (
                <Button
                  data-testid="remove-button"
                  size="small"
                  variant="tertiary"
                  onClick={() => removeFile(file.id)}
                >
                  <Icon
                    height={20}
                    width={20}
                    icon="plus"
                    className={styles.removeIcon}
                  />
                </Button>
              )}
            </div>
            {file.errorReason && !isRejected && (
              <div className={styles.errorMessageContainer}>
                <p className={styles.errorMessage}>{file.errorReason}</p>
              </div>
            )}
            {!file.errorReason && isRejected && (
              <div className={styles.errorMessageContainer}>
                <p
                  className={clsx(styles.errorMessage, styles.rejectedMessage)}
                >
                  {file.rejectReason}
                </p>
              </div>
            )}
          </div>
        );
      })}
    </>
  );
};
